
import { Component, ElementRef, HostListener, OnDestroy, OnInit, effect, inject, input, output, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    finalize,
    switchMap,
    takeUntil,
} from 'rxjs/operators';
import {
    MentionSuggestion,
    NotesWithMentionsDto,
    TimeOffPolicyNoteType,
} from '../../../models/notes.models';
import { NotesService } from '../../../services/notes.service';
import { ResourceService } from '../../../services/resource.service';
import { SnackbarService } from '../../../services/snackbar.service';
import {
    UnsavedChangesDialogComponent,
    UnsavedChangesDialogData,
} from '../unsaved-changes-dialog/unsaved-changes-dialog.component';

@Component({
  selector: 'app-entity-notes',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule
],
  templateUrl: './entity-notes.component.html',
  styleUrls: ['./entity-notes.component.scss'],
})
export class EntityNotesComponent implements OnInit, OnDestroy {
  private notesService = inject(NotesService);
  private snackbarService = inject(SnackbarService);
  private dialog = inject(MatDialog);
  private resourceService = inject(ResourceService);
  private router = inject(Router);

  readonly entityId = input.required<string>(); // employeeId for resources, clientMatterNumber for matters
  readonly entityType = input<'resource' | 'matter' | 'assignment' | 'timeoff' | 'timeoff-policy'>('resource'); // Type of entity for notes
  readonly apiMode = input<'admin' | 'self'>('admin'); // API mode for timeoff notes (admin vs self-service)
  readonly policyNoteType = input<TimeOffPolicyNoteType | null>(null); // Required when entityType is 'timeoff-policy'
  readonly notesInfoInput = input<NotesWithMentionsDto | null | undefined>(undefined, { alias: 'notesInfo' }); // Notes information from parent (e.g., from ResourceDetailsDto.notesInfo)
  protected notesInfo: NotesWithMentionsDto | null | undefined;
  readonly title = input<string>('Notes');
  readonly placeholder = input<string>('Click Edit to add notes...');
  readonly isReadOnly = input<boolean>(false);
  readonly pendingMode = input<boolean>(false); // When true, skip API load/save and emit raw content on save
  readonly breadcrumbContext = input<{
    label: string;
    url: string;
}[]>([]);

  readonly notesSaved = output<NotesWithMentionsDto>(); // Emit updated notes with mentions after save
  readonly editModeRequested = output<void>(); // Emit when user wants to enter edit mode
  readonly mentionNavigated = output<void>(); // Emit when a mention link is clicked (for dialog close)

  readonly editorRef = viewChild<ElementRef<HTMLDivElement>>('editor');

  isLoading = false; // No longer loading from API, content comes from parent
  isSaving = false;
  isEditMode = false;
  error: string | null = null;

  // Track content for unsaved changes detection
  private originalContent: string = '';
  private currentContent: string = '';
  private destroy$ = new Subject<void>();

  // Editor content - set once when entering edit mode to avoid cursor reset
  editorInitialContent: string = '';

  // @mention functionality
  showMentionDropdown = false;
  mentionSuggestions: MentionSuggestion[] = [];
  mentionSearchTerm = '';
  selectedMentionIndex = 0;
  isSearchingMentions = false;
  private mentionSearch$ = new Subject<string>();
  private mentionStartPosition: { node: Node; offset: number } | null = null;
  dropdownPosition = { top: 0, left: 0 };

  ngOnInit(): void {
    this.initializeContent();
    this.setupMentionSearch();

    if (this.pendingMode()) {
      // In pending mode, start directly in edit mode
      setTimeout(() => this.enterEditMode());
    }
  }

  constructor() {
    let isFirst = true;
    effect(() => {
      this.notesInfo = this.notesInfoInput();
      if (!isFirst) {
        this.initializeContent();
      }
      isFirst = false;
    });
  }

  private setupMentionSearch(): void {
    this.mentionSearch$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((term) => {
          if (!term || term.length === 0) {
            return [];
          }
          this.isSearchingMentions = true;
          return this.resourceService.searchResourcesForMention(term);
        }),
      )
      .subscribe({
        next: (resources) => {
          this.mentionSuggestions = resources.map((r) => ({
            employeeId: r.employeeId,
            fullName: r.fullName,
            title: r.title,
          }));
          this.selectedMentionIndex = 0;
          this.isSearchingMentions = false;
        },
        error: () => {
          this.mentionSuggestions = [];
          this.isSearchingMentions = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onNotesViewClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const mentionLink = target.closest('.mention-link') as HTMLAnchorElement;
    if (mentionLink && mentionLink.href) {
      event.preventDefault();
      event.stopPropagation();
      // Extract employee ID from href: #/resources/12345 -> 12345
      const hrefParts = mentionLink.href.split('/resources/');
      if (hrefParts.length > 1) {
        const employeeId = hrefParts[1];
        this.mentionNavigated.emit();
        const breadcrumbContext = this.breadcrumbContext();
        const state = breadcrumbContext.length
          ? {
              breadcrumbOverride: [
                ...breadcrumbContext,
                { label: ':employeeId', url: '' },
              ],
            }
          : undefined;
        this.router.navigate(['/resources', employeeId], { state });
      }
    }
  }

  private initializeContent(): void {
    this.originalContent = this.notesInfo?.notes || '';
    this.currentContent = this.originalContent;
  }

  toggleEditMode(): void {
    if (this.isEditMode) {
      // Exiting edit mode - check for unsaved changes
      if (this.hasUnsavedChanges()) {
        this.showUnsavedChangesDialog();
        return;
      }
      this.isEditMode = false;
      return;
    }

    // Request to enter edit mode - parent will coordinate
    this.editModeRequested.emit();
  }

  /**
   * Called by parent to actually enter edit mode after coordination
   */
  enterEditMode(): void {
    this.isEditMode = true;

    // Entering edit mode - store original content
    this.originalContent = this.notesInfo?.notes || '';

    // Convert keywords to badges for editing
    const contentWithBadges = this.convertKeywordsToBadges(
      this.originalContent,
    );

    // Set editor content once to avoid constant rebinding which resets cursor
    // Also ensure all badges have contenteditable="false" for atomic behavior
    // Angular's innerHTML binding will sanitize automatically
    this.editorInitialContent = this.ensureBadgesAreAtomic(contentWithBadges);

    // Set default paragraph separator to prevent browser quirks with bold tags
    document.execCommand('defaultParagraphSeparator', false, 'p');
    // Focus editor after view updates and place cursor at end
    setTimeout(() => {
      const editorRef = this.editorRef();
      if (editorRef?.nativeElement) {
        editorRef.nativeElement.focus({ preventScroll: true });
        this.placeCursorAtEnd();
      }
    });
  }

  private placeCursorAtEnd(): void {
    const editor = this.editorRef()?.nativeElement;
    if (!editor) return;

    const selection = window.getSelection();
    if (selection) {
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false); // collapse to end
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  /**
   * Try to exit edit mode, showing dialog if there are unsaved changes.
   * Returns a promise that resolves to true if exited, false if user cancelled.
   */
  tryExitEditMode(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.isEditMode) {
        resolve(true);
        return;
      }

      if (!this.hasUnsavedChanges()) {
        this.isEditMode = false;
        resolve(true);
        return;
      }

      const dialogData: UnsavedChangesDialogData = {
        entityName: 'Notes',
        hasValidationErrors: false,
      };

      const dialogRef = this.dialog.open(UnsavedChangesDialogComponent, {
        width: '500px',
        disableClose: true,
        data: dialogData,
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'save') {
          this.saveNoteAndResolve(resolve);
        } else if (result === 'discard') {
          this.discardChanges();
          resolve(true);
        } else {
          // 'cancel' - don't exit
          resolve(false);
        }
      });
    });
  }

  cancelEdit(): void {
    if (this.hasUnsavedChanges()) {
      this.showUnsavedChangesDialog();
      return;
    }
    this.discardChanges();
  }

  private discardChanges(): void {
    // Restore original content
    this.currentContent = this.originalContent;
    const editorRef = this.editorRef();
    if (editorRef?.nativeElement) {
      editorRef.nativeElement.innerHTML = this.originalContent;
    }
    this.isEditMode = false;
  }

  private showUnsavedChangesDialog(): void {
    const dialogData: UnsavedChangesDialogData = {
      entityName: 'Notes',
      hasValidationErrors: false,
    };

    const dialogRef = this.dialog.open(UnsavedChangesDialogComponent, {
      width: '500px',
      disableClose: true,
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'save') {
        this.saveNote();
      } else if (result === 'discard') {
        this.discardChanges();
      }
      // 'cancel' - do nothing, stay in edit mode
    });
  }

  /**
   * Check if there are unsaved changes that need to be handled before navigation.
   * This method can be called by parent components or route guards.
   */
  checkUnsavedChangesForNavigation(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.hasUnsavedChanges()) {
        resolve(true);
        return;
      }

      const dialogData: UnsavedChangesDialogData = {
        entityName: 'Notes',
        hasValidationErrors: false,
      };

      const dialogRef = this.dialog.open(UnsavedChangesDialogComponent, {
        width: '500px',
        disableClose: true,
        data: dialogData,
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'save') {
          // Save and then allow navigation
          this.saveNoteAndResolve(resolve);
        } else if (result === 'discard') {
          this.discardChanges();
          resolve(true);
        } else {
          // 'cancel' - don't allow navigation
          resolve(false);
        }
      });
    });
  }

  private saveNoteAndResolve(resolve: (value: boolean) => void): void {
    if (this.isSaving) {
      resolve(false);
      return;
    }

    if (this.pendingMode()) {
      const content = this.editorRef()?.nativeElement?.innerHTML || '';
      const contentWithKeywords = this.convertBadgesToKeywords(content);
      this.originalContent = contentWithKeywords;
      this.currentContent = contentWithKeywords;
      this.isEditMode = false;
      this.notesSaved.emit({ notes: contentWithKeywords, mentions: [] });
      resolve(true);
      return;
    }

    this.performSave()
      .then(() => resolve(true))
      .catch(() => resolve(false));
  }

  saveNote(): void {
    if (this.isSaving) return;

    const content = this.editorRef()?.nativeElement?.innerHTML || '';
    const contentWithKeywords = this.convertBadgesToKeywords(content);

    // Don't save if content hasn't changed
    if (contentWithKeywords === this.originalContent) {
      this.isEditMode = false;
      return;
    }

    if (this.pendingMode()) {
      // In pending mode, emit raw content without calling API
      this.originalContent = contentWithKeywords;
      this.currentContent = contentWithKeywords;
      this.isEditMode = false;
      this.notesSaved.emit({ notes: contentWithKeywords, mentions: [] });
      return;
    }

    this.performSave();
  }

  /**
   * Performs the actual save operation.
   * Returns a promise that resolves on success and rejects on error.
   */
  private performSave(): Promise<void> {
    return new Promise((resolve, reject) => {
      const content = this.editorRef()?.nativeElement?.innerHTML || '';
      const contentWithKeywords = this.convertBadgesToKeywords(content);

      this.isSaving = true;

      const entityType = this.entityType();
      const saveObservable =
        entityType === 'matter'
          ? this.notesService.updateMatterNotes(
              this.entityId(),
              contentWithKeywords,
            )
          : entityType === 'assignment'
            ? this.notesService.updateAssignmentNotes(
                Number(this.entityId()),
                contentWithKeywords,
              )
            : entityType === 'timeoff-policy'
              ? this.notesService.updateResourcePolicyNote(
                  this.entityId(),
                  this.policyNoteType()!,
                  contentWithKeywords,
                )
              : entityType === 'timeoff'
                ? this.apiMode() === 'self'
                  ? this.notesService.updateMyTimeOffNotes(
                      Number(this.entityId()),
                      contentWithKeywords,
                    )
                  : this.notesService.updateTimeOffNotes(
                      Number(this.entityId()),
                      contentWithKeywords,
                    )
                : this.notesService.updateResourceNotes(
                    this.entityId(),
                    contentWithKeywords,
                  );

      saveObservable
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => (this.isSaving = false)),
        )
        .subscribe({
          next: (savedNoteWithMentions) => {
            this.originalContent = savedNoteWithMentions.notes || '';
            this.currentContent = this.originalContent;
            // Update notesInfo with the new data from backend
            this.notesInfo = savedNoteWithMentions;
            this.isEditMode = false;
            this.notesSaved.emit(savedNoteWithMentions);
            this.snackbarService.showSuccess('Notes saved successfully');
            resolve();
          },
          error: (err) => {
            console.error('Error saving note:', err);
            this.snackbarService.showError('Failed to save notes');
            reject(err);
          },
        });
    });
  }

  onContentChange(): void {
    const editorRef = this.editorRef();
    if (editorRef?.nativeElement) {
      this.currentContent = editorRef.nativeElement.innerHTML;
    }

    // Check for @mention trigger
    this.checkForMentionTrigger();
  }

  onEditorMouseDown(event: MouseEvent): void {
    // No special handling needed - let the browser handle selection
  }

  onEditorClick(event: MouseEvent): void {
    // Update badge selection state
    this.updateBadgeSelectionState();
  }

  private updateBadgeSelectionState(): void {
    const editor = this.editorRef()?.nativeElement;
    if (!editor) return;

    // Remove selected class from all badges
    const allBadges = editor.querySelectorAll('.mention-badge');
    allBadges.forEach((badge: Element) => badge.classList.remove('selected'));

    // Check if any badge is in the current selection
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);

    // Check each badge to see if it's within or is the selection
    allBadges.forEach((badge: Element) => {
      if (range.intersectsNode(badge) || selection.containsNode(badge, true)) {
        badge.classList.add('selected');
      }
    });
  }

  private checkForMentionTrigger(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const textNode = range.startContainer;

    if (textNode.nodeType !== Node.TEXT_NODE) {
      this.closeMentionDropdown();
      return;
    }

    const text = textNode.textContent || '';
    const cursorPos = range.startOffset;

    // Find the last @ symbol before cursor
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex === -1) {
      this.closeMentionDropdown();
      return;
    }

    // Check if there's a space between @ and cursor (which would cancel the mention)
    const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
    if (textAfterAt.includes(' ')) {
      this.closeMentionDropdown();
      return;
    }

    // We have an active mention trigger
    this.mentionSearchTerm = textAfterAt;
    this.mentionStartPosition = { node: textNode, offset: lastAtIndex };

    // Position the dropdown
    this.positionDropdown();

    // Show dropdown and search
    this.showMentionDropdown = true;
    this.mentionSearch$.next(this.mentionSearchTerm);
  }

  private positionDropdown(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const editorRect = this.editorRef()?.nativeElement?.getBoundingClientRect();

    if (editorRect) {
      this.dropdownPosition = {
        top: rect.bottom - editorRect.top + 4,
        left: rect.left - editorRect.left,
      };
    }
  }

  closeMentionDropdown(): void {
    this.showMentionDropdown = false;
    this.mentionSuggestions = [];
    this.mentionSearchTerm = '';
    this.mentionStartPosition = null;
    this.selectedMentionIndex = 0;
  }

  onEditorKeyDown(event: KeyboardEvent): void {
    // Handle backspace to delete selected badge
    if (event.key === 'Backspace') {
      const selectedBadge = this.editorRef()?.nativeElement?.querySelector(
        '.mention-badge.selected',
      );
      if (selectedBadge) {
        event.preventDefault();

        // Select the badge programmatically so execCommand('delete') can remove it
        const selection = window.getSelection();
        if (selection) {
          const range = document.createRange();
          range.selectNode(selectedBadge);
          selection.removeAllRanges();
          selection.addRange(range);

          // Use execCommand to delete - this preserves the undo stack
          document.execCommand('delete', false);
        }

        this.onContentChange();
        this.updateBadgeSelectionState();
        return;
      }
    }

    if (this.showMentionDropdown) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.selectedMentionIndex = Math.min(
            this.selectedMentionIndex + 1,
            this.mentionSuggestions.length - 1,
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.selectedMentionIndex = Math.max(
            this.selectedMentionIndex - 1,
            0,
          );
          break;
        case 'Enter':
        case 'Tab':
          if (this.mentionSuggestions.length > 0) {
            event.preventDefault();
            this.selectMention(
              this.mentionSuggestions[this.selectedMentionIndex],
            );
          }
          break;
        case 'Escape':
          event.preventDefault();
          this.closeMentionDropdown();
          break;
      }
    }
  }

  selectMention(mention: MentionSuggestion): void {
    if (!this.mentionStartPosition) return;

    const selection = window.getSelection();
    if (!selection) return;

    // Create the mention badge element with keyword format @{EmployeeId}
    const mentionSpan = document.createElement('span');
    mentionSpan.className = 'mention-badge';
    mentionSpan.contentEditable = 'false';
    mentionSpan.setAttribute('data-employee-id', mention.employeeId);
    mentionSpan.setAttribute('data-mention', 'true');
    // Display the full name, but the keyword will be used when saving
    mentionSpan.textContent = mention.fullName;

    // Find and replace the @searchTerm with the badge
    const textNode = this.mentionStartPosition.node as Text;
    const text = textNode.textContent || '';
    const beforeAt = text.substring(0, this.mentionStartPosition.offset);

    // Calculate end position based on stored mention data, not current cursor
    // The @ symbol is at mentionStartPosition.offset, and the search term follows it
    const endOfMention =
      this.mentionStartPosition.offset + 1 + this.mentionSearchTerm.length;
    const afterMention = text.substring(endOfMention);

    // Create text nodes for before and after
    const beforeTextNode = document.createTextNode(beforeAt);
    const afterTextNode = document.createTextNode('\u00A0' + afterMention); // Add non-breaking space after

    // Replace the text node with our new nodes
    const parent = textNode.parentNode;
    if (parent) {
      parent.insertBefore(beforeTextNode, textNode);
      parent.insertBefore(mentionSpan, textNode);
      parent.insertBefore(afterTextNode, textNode);
      parent.removeChild(textNode);

      // Position cursor after the mention
      const newRange = document.createRange();
      newRange.setStart(afterTextNode, 1); // After the non-breaking space
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }

    this.closeMentionDropdown();
    this.onContentChange();
    this.editorRef()?.nativeElement?.focus();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const dropdown = document.querySelector('.mention-dropdown');
    const editor = this.editorRef()?.nativeElement;

    if (
      dropdown &&
      !dropdown.contains(target) &&
      editor &&
      !editor.contains(target)
    ) {
      this.closeMentionDropdown();
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    // Get plain text from clipboard
    const text = event.clipboardData?.getData('text/plain') || '';

    // Insert as plain text (this preserves line breaks as actual line breaks)
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();

      // Convert line breaks to <br> tags for proper display
      const lines = text.split('\n');
      const fragment = document.createDocumentFragment();

      lines.forEach((line, index) => {
        fragment.appendChild(document.createTextNode(line));
        if (index < lines.length - 1) {
          fragment.appendChild(document.createElement('br'));
        }
      });

      range.insertNode(fragment);

      // Move cursor to end of inserted content
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    this.onContentChange();
  }

  hasUnsavedChanges(): boolean {
    if (!this.isEditMode) return false;
    const currentEditorContent = this.editorRef()?.nativeElement?.innerHTML || '';
    return currentEditorContent !== this.originalContent;
  }

  // Rich text formatting commands
  formatBold(): void {
    document.execCommand('bold', false);
    this.editorRef()?.nativeElement?.focus();
    this.updateFormattingState();
  }

  formatItalic(): void {
    document.execCommand('italic', false);
    this.editorRef()?.nativeElement?.focus();
    this.updateFormattingState();
  }

  formatUnderline(): void {
    document.execCommand('underline', false);
    this.editorRef()?.nativeElement?.focus();
    this.updateFormattingState();
  }

  formatBulletList(): void {
    document.execCommand('insertUnorderedList', false);
    this.editorRef()?.nativeElement?.focus();
    this.updateFormattingState();
  }

  formatNumberedList(): void {
    document.execCommand('insertOrderedList', false);
    this.editorRef()?.nativeElement?.focus();
    this.updateFormattingState();
  }

  clearFormatting(): void {
    document.execCommand('removeFormat', false);
    this.editorRef()?.nativeElement?.focus();
    this.updateFormattingState();
  }

  // Track active formatting state
  isBoldActive = false;
  isItalicActive = false;
  isUnderlineActive = false;
  isBulletListActive = false;
  isNumberedListActive = false;

  updateFormattingState(): void {
    this.isBoldActive = document.queryCommandState('bold');
    this.isItalicActive = document.queryCommandState('italic');
    this.isUnderlineActive = document.queryCommandState('underline');
    this.isBulletListActive = document.queryCommandState('insertUnorderedList');
    this.isNumberedListActive = document.queryCommandState('insertOrderedList');
  }

  onSelectionChange(): void {
    this.updateFormattingState();
    this.updateBadgeSelectionState();
  }

  // Check if content is empty (for display purposes)
  get hasContent(): boolean {
    const content = this.notesInfo?.notes || '';
    // Strip HTML tags and check if there's actual text
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    return textContent.length > 0;
  }

  // Get display content
  get displayContent(): string {
    const content = this.notesInfo?.notes || '';

    if (this.isEditMode) {
      // In edit mode, should not reach here as we use editorInitialContent
      return content;
    }

    // In view mode, transform mention keywords to clickable links
    return this.convertKeywordsToLinks(content);
  }

  // Get last updated information for display
  get lastUpdatedInfo(): string | null {
    const lastUpdatedBy = this.notesInfo?.lastUpdatedBy;
    if (!lastUpdatedBy || !lastUpdatedBy.timestamp) {
      return null;
    }

    const firstName = lastUpdatedBy.firstName || '';
    const lastName = lastUpdatedBy.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();

    // Backend sends DateTimeOffset with timezone information
    const date = new Date(lastUpdatedBy.timestamp);
    const formattedDate = date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    return fullName
      ? `Last updated by ${fullName} on ${formattedDate}`
      : `Last updated on ${formattedDate}`;
  }

  /**
   * Convert mention patterns to clickable links for display, using mention.pattern for literal replacement
   */
  private convertKeywordsToLinks(content: string): string {
    if (!content) return '';

    let result = content;
    const mentions = this.notesInfo?.mentions || [];
    for (const mention of mentions) {
      const link = `<a class="mention-link" href="#/resources/${mention.entityId}">${mention.displayName}</a>`;
      result = result.replaceAll(mention.pattern, link);
    }
    return result;
  }

  /**
   * Escape HTML special characters to prevent XSS
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Convert mention patterns to badge elements for editing, using mention.pattern for literal replacement
   */
  private convertKeywordsToBadges(content: string): string {
    if (!content) return '';

    let result = content;
    const mentions = this.notesInfo?.mentions || [];
    for (const mention of mentions) {
      const badge = `<a class="mention-badge" href="#/resources/${mention.entityId}">${mention.displayName}</a>`;
      result = result.replaceAll(mention.pattern, badge);
    }
    return result;
  }

  /**
   * Convert badge elements back to keyword format @{EmployeeId} for saving
   */
  private convertBadgesToKeywords(content: string): string {
    if (!content) return '';

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');

    const mentionBadges = doc.querySelectorAll('.mention-badge');
    mentionBadges.forEach((badge) => {
      let employeeId: string | null = null;

      // Try to get employee ID from data-employee-id attribute (newly added mentions)
      employeeId = badge.getAttribute('data-employee-id');

      // If not found, try to extract from href attribute (loaded mentions)
      if (!employeeId) {
        const href = badge.getAttribute('href');
        if (href) {
          // Extract employee ID from href: #/resources/12345 -> 12345
          const hrefParts = href.split('/resources/');
          if (hrefParts.length > 1) {
            employeeId = hrefParts[1];
          }
        }
      }

      // If we found an employee ID, replace the badge with the keyword format
      if (employeeId) {
        const keyword = document.createTextNode(`@{${employeeId}}`);
        badge.parentNode?.replaceChild(keyword, badge);
      }
    });

    return doc.body.innerHTML;
  }

  private ensureBadgesAreAtomic(content: string): string {
    // Ensure all mention badges have contenteditable="false" for atomic behavior
    // and have text nodes around them for proper cursor navigation
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');

    const mentionBadges = doc.querySelectorAll('.mention-badge');
    mentionBadges.forEach((badge) => {
      badge.setAttribute('contenteditable', 'false');

      // Ensure there's a text node before the badge for cursor positioning
      if (
        !badge.previousSibling ||
        badge.previousSibling.nodeType !== Node.TEXT_NODE
      ) {
        const textBefore = doc.createTextNode('\u200B'); // Zero-width space
        badge.parentNode?.insertBefore(textBefore, badge);
      }

      // Ensure there's a text node after the badge for cursor positioning
      if (!badge.nextSibling || badge.nextSibling.nodeType !== Node.TEXT_NODE) {
        const textAfter = doc.createTextNode('\u200B'); // Zero-width space
        badge.parentNode?.insertBefore(textAfter, badge.nextSibling);
      }
    });

    return doc.body.innerHTML;
  }
}
